/**
 * API client for chapter annotations endpoints.
 * Annotations are icons (Watch, Explore, Practice, Curiosity) placed on PDFs by superadmins.
 */
import axios from 'axios';
import { getAuthToken, getPortalType } from '@/utils/auth';
import { apiUrl } from './config';

/**
 * Get authentication headers
 */
const getAuthHeaders = () => {
  const token = getAuthToken(getPortalType(window.location.pathname || ''));
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

/**
 * Chapter Annotations API
 */
export const chapterAnnotationsAPI = {
  /**
   * Get all annotations for a specific chapter
   * @param {number} chapterId - Chapter ID
   * @returns {Promise} Annotations list for chapter
   */
  getChapterAnnotations: async (chapterId) => {
    try {
      const response = await axios.get(
        apiUrl(`superadmin/chapter-annotations/?chapter=${chapterId}`),
        { headers: getAuthHeaders() }
      );
      // Handle paginated response
      return response.data.results || response.data || [];
    } catch (error) {
      console.error('Error fetching chapter annotations:', error);
      throw error;
    }
  },
  
  /**
   * Get practice questions for a specific annotation
   * @param {number} annotationId - Annotation ID
   * @returns {Promise} Practice questions object
   */
  getPracticeQuestions: async (annotationId) => {
    try {
      const response = await axios.get(
        apiUrl(`superadmin/chapter-annotations/${annotationId}/questions/`),
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching practice questions:', error);
      throw error;
    }
  },
  
  /**
   * Get curiosity content for a specific annotation
   * @param {number} annotationId - Annotation ID
   * @returns {Promise} Curiosity content object
   */
  getCuriosityContent: async (annotationId) => {
    try {
      const response = await axios.get(
        apiUrl(`superadmin/chapter-annotations/${annotationId}/curiosity-content/`),
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching curiosity content:', error);
      throw error;
    }
  },
  
  /**
   * Get explore simulation for a specific annotation
   * @param {number} annotationId - Annotation ID
   * @returns {Promise} Explore simulation object
   */
  getExploreSimulation: async (annotationId) => {
    try {
      const response = await axios.get(
        apiUrl(`superadmin/chapter-annotations/${annotationId}/explore-simulation/`),
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching explore simulation:', error);
      throw error;
    }
  },
  
  /**
   * Get watch content for a specific annotation
   * @param {number} annotationId - Annotation ID
   * @returns {Promise} Watch content object
   */
  getWatchContent: async (annotationId) => {
    try {
      const response = await axios.get(
        apiUrl(`superadmin/chapter-annotations/${annotationId}/watch-content/`),
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching watch content:', error);
      throw error;
    }
  },

  /**
   * Get a single annotation (used to retrieve video_file_url for Video/Watch)
   */
  getAnnotation: async (annotationId) => {
    try {
      const response = await axios.get(
        apiUrl(`superadmin/chapter-annotations/${annotationId}/`),
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching annotation:', error);
      throw error;
    }
  },
  
  /**
   * Update annotation position
   * @param {number} annotationId - Annotation ID
   * @param {number} positionTop - Top position as percentage (0-100)
   * @param {number} positionLeft - Left position as percentage (0-100)
   * @returns {Promise} Updated annotation
   */
  updatePosition: async (annotationId, positionTop, positionLeft) => {
    try {
      const response = await axios.patch(
        apiUrl(`superadmin/chapter-annotations/${annotationId}/`),
        {
          position_top: positionTop,
          position_left: positionLeft
        },
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating annotation position:', error);
      throw error;
    }
  },
};

